import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnDestroy,
  OnInit
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatSidenavModule } from '@angular/material/sidenav';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  animate,
  state,
  style,
  transition,
  trigger
} from '@angular/animations';
import { AppRoutingModule } from 'src/app/app-routing.module';
import { AuthService } from '../../services/auth/auth.service';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import { MenuItem } from 'src/app/interfaces/menu-item.interface';
import { MENU_ITEMS } from 'src/app/app.constants';
import {
  ActivatedRouteSnapshot,
  NavigationStart,
  Router
} from '@angular/router';
import { SweetAlertService } from 'src/app/services/sweet-alert/sweet-alert.service';

@Component({
  selector: 'app-sidenav',
  templateUrl: './sidenav.component.html',
  styleUrls: ['./sidenav.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatSidenavModule,
    TranslateModule,
    AppRoutingModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatListModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('fadeSlideInOut', [
      state(
        'in',
        style({
          opacity: 1,
          transform: 'translateX(0)'
        })
      ),
      transition(':enter', [
        style({
          opacity: 0,
          transform: 'translateX(-100%)'
        }),
        animate('300ms ease-in')
      ]),
      transition(':leave', [
        animate(
          '300ms ease-out',
          style({
            opacity: 0,
            transform: 'translateX(100%)'
          })
        )
      ])
    ])
  ]
})
export class SidenavComponent implements OnInit, OnDestroy {
  @Input() openedSidenav: boolean = false;

  public isLogged$: BehaviorSubject<boolean> = this.authService.userLogged();
  public showBackButton$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public menuItems: MenuItem[] = MENU_ITEMS;
  public showBackButton: boolean = false;

  private readonly onDestroy$: Subject<void> = new Subject();
  private readonly lastRouteKey = 'lastRoute';
  private currentUrl: string | null = null;

  constructor(
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly translateService: TranslateService,
    private readonly sweetAlertService: SweetAlertService
  ) {}

  public ngOnInit(): void {
    const regex = /^\/doubts(\/.*)?$/;
    const regex2 = /^\/register(\/.*)?$/;

    this.router.events.pipe(takeUntil(this.onDestroy$)).subscribe((event) => {
      //todo: crear una lista con las urls que no tienen el botón volver
      const url = this.router.url;
      const paths = this.menuItems.map((item) => '/' + item.path);
      this.showBackButton$.next(
        paths.some((path) => path === url) ||
          url === '/home' ||
          regex.test(url) ||
          url === '/' ||
          url === '/login' ||
          regex2.test(url)
      );
      if (event instanceof NavigationStart) {
        if (this.currentUrl) {
          localStorage.setItem(this.lastRouteKey, this.currentUrl);
        }
        this.currentUrl = event.url;
      }
    });
  }

  public logout(): void {
    this.authService.logout();
  }

  public async goBack() {
    const route: ActivatedRouteSnapshot = this.router.routerState.snapshot.root;
    const segments =
      route.children[0]?.url.map((segment) => segment.path) || [];
    const url = segments.join('/');
    const regex = /^\/(teams|reviews)\/create\/[^\/]+$/;
    let isConfirmed = true;
    if (regex.test(this.router.url)) {
      const result = await this.sweetAlertService.showAlert({
        title: `${this.translateService.instant('COMMON.ARE_YOU_SURE_YOU_WANT_TO_EXIT')}`,
        showDenyButton: true,
        confirmButtonText: this.translateService.instant('COMMON.YES'),
        denyButtonText: this.translateService.instant('COMMON.NO'),
        icon: 'warning'
      });
      isConfirmed = result.isConfirmed;
    }

    if (isConfirmed) {
      if (url.includes('clients-control')) {
        this.navigateToLastRoute();
      } else {
        this.router.navigate([url]);
      }
    }
    // this.router.navigate([this.truncateString(this.router.url)]);
  }

  public ngOnDestroy(): void {
    this.onDestroy$.complete();
    this.onDestroy$.unsubscribe();
  }

  private getLastRoute(): string | null {
    return localStorage.getItem(this.lastRouteKey);
  }

  private navigateToLastRoute(): void {
    const lastRoute = this.getLastRoute();
    if (lastRoute) {
      this.router.navigateByUrl(lastRoute);
      // this.router.navigate([lastRoute]);
    } else {
      this.router.navigate(['/']); // Ruta por defecto
    }
  }

  // private truncateString(str: string): string {
  // Divide el string en partes usando el carácter como separador
  // const parts = str.split('/');

  // Regresa la primera parte unida con el carácter, si es que hay más partes
  //   return parts.length > 1 ? parts.slice(0, -1).join('/') : str;
  // }
}
